// Single source of truth for query-level LLM model selection, shared by the
// search backend, the dev picker, and the test harness. A profile is a named
// curated mix of models across roles; alias strings resolve via
// `resolveModelAlias` (apps/search services/model-catalog.ts).

export const MODEL_ROLES = [
  'general',
  'cheap',
  'cheapStructured',
  'webSearch',
  'image',
] as const

export type ModelRole = (typeof MODEL_ROLES)[number]

// Roles resolved as a [primary, fallback] chain. The pinned roles (webSearch,
// image) run a single model, so no fallback selection is accepted for them.
export const FALLBACK_ROLES = ['general', 'cheap', 'cheapStructured'] as const

/** One role's model selection, as aliases. An absent slot keeps that slot's
 *  server default, so a selection can override a primary, a fallback, or both. */
export type RoleChain = { primary?: string; fallback?: string }

/** A run's per-role model selection — what the catalog resolves each role's
 *  `{primary, fallback}` chain against. */
export type ModelLoadout = Partial<Record<ModelRole, RoleChain>> & {
  /** Model for a consulted sub-agent, independent of `general`; absent ⇒ the
   *  server default, which falls back to `general`. A chain names its own
   *  fallback instead. */
  delegateGeneral?: string | RoleChain
}

type ModelProfile = {
  id: string
  label: string
  loadout: ModelLoadout
}

// Sentinel profile id: ignore the org default and keep the route's own default
// models. Distinct from "no selection" (undefined), which keeps the org default.
export const SERVER_DEFAULT_PROFILE_ID = 'server-default'

// Runs every role's primary on Gemini, with Claude fallbacks on the chain
// roles (general/cheap/cheapStructured): the Standard and Simple efforts,
// and the agent-kernel route default.
export const GEMINI_MODEL_PROFILE_ID = 'gemini'

// Claude on the reasoning roles over the Gemini loadout. It sets every role, so
// Extended fully replaces an org default such as a profile under test.
/** @knipignore */
export const ANTHROPIC_MODEL_PROFILE_ID = 'anthropic'

// The Gemini profile with the reasoning loop on the lite model: regular Ask
// on the kernel and the Simple effort — one search, one answer, where the
// eval showed no quality loss for a cheaper and faster run.
export const GEMINI_LITE_MODEL_PROFILE_ID = 'gemini-lite'

const GEMINI_LOADOUT: ModelLoadout = {
  general: { primary: 'gemini-3-8-flash', fallback: 'google-sonnet-4-6' },
  cheap: { primary: 'gemini-3-5-flash-lite', fallback: 'google-haiku-4-5' },
  cheapStructured: {
    primary: 'gemini-3-8-flash',
    fallback: 'google-haiku-4-5',
  },
  webSearch: { primary: 'gemini-3-5-flash-lite' },
  image: { primary: 'gemini-2-5-flash-image' },
  delegateGeneral: 'gemini-3-8-flash',
}

export const MODEL_PROFILES: ModelProfile[] = [
  {
    id: GEMINI_MODEL_PROFILE_ID,
    label: 'Gemini (all roles)',
    loadout: GEMINI_LOADOUT,
  },
  {
    id: GEMINI_LITE_MODEL_PROFILE_ID,
    label: 'Gemini, Flash Lite reasoning',
    loadout: {
      ...GEMINI_LOADOUT,
      general: {
        primary: 'gemini-3-5-flash-lite',
        fallback: 'gemini-3-8-flash',
      },
    },
  },
  {
    id: ANTHROPIC_MODEL_PROFILE_ID,
    label: 'Anthropic (Claude)',
    loadout: {
      ...GEMINI_LOADOUT,
      general: { primary: 'google-sonnet-4-6', fallback: 'gemini-3-8-flash' },
      cheapStructured: {
        primary: 'google-haiku-4-5',
        fallback: 'gemini-3-8-flash',
      },
    },
  },
  {
    id: 'haiku',
    label: 'Haiku 5.5 on the Gemini reasoning, cheap and delegate roles',
    loadout: {
      ...GEMINI_LOADOUT,
      general: { primary: 'google-haiku-5-5', fallback: 'gemini-3-8-flash' },
      cheap: { primary: 'google-haiku-5-5', fallback: 'gemini-3-5-flash-lite' },
      cheapStructured: {
        primary: 'google-haiku-5-5',
        fallback: 'gemini-3-8-flash',
      },
      delegateGeneral: {
        primary: 'google-haiku-5-5',
        fallback: 'gemini-3-8-flash',
      },
    },
  },
]

export const getModelProfile = (id: string): ModelProfile | undefined =>
  MODEL_PROFILES.find((profile) => profile.id === id)

/** How hard a Super round works. Simple runs the kernel's `ask` agent
 *  (Slite docs only, one search, no plan) on the Flash Lite profile; Standard
 *  and Extended run the thread's own agent and differ by model profile. */
export const EFFORTS = ['simple', 'standard', 'extended'] as const

export type Effort = (typeof EFFORTS)[number]

export const isEffort = (value: unknown): value is Effort =>
  EFFORTS.includes(value as Effort)

export const MODEL_PROFILE_BY_EFFORT: Record<Effort, string> = {
  simple: GEMINI_LITE_MODEL_PROFILE_ID,
  standard: GEMINI_MODEL_PROFILE_ID,
  extended: ANTHROPIC_MODEL_PROFILE_ID,
}

export const DEFAULT_EFFORT: Effort = 'standard'

/** The effort a default model profile (the user's, else the org's) stands for. */
export const effortFromModelProfile = (
  modelProfile: string | undefined
): Effort =>
  modelProfile === MODEL_PROFILE_BY_EFFORT.extended
    ? 'extended'
    : DEFAULT_EFFORT

const EFFORT_PROFILE_IDS: readonly string[] = Object.values(
  MODEL_PROFILE_BY_EFFORT
)

/** The profile a run takes for its effort: a default (the user's, else the
 *  org's) that no effort stands for (a profile under test) replaces the default
 *  effort's, so it runs while the other efforts stay a per-question opt-out.
 *  Undefined leaves the effort's own profile. */
export const modelProfileForEffort = (
  effort: Effort | undefined,
  defaultModelProfile: string | undefined
): string | undefined =>
  effort === DEFAULT_EFFORT &&
  defaultModelProfile &&
  !EFFORT_PROFILE_IDS.includes(defaultModelProfile)
    ? defaultModelProfile
    : undefined
